import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, Check } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import ConsultationForm from "@/components/site/ConsultationForm";
import { Reveal, MaskLine } from "@/components/site/Reveal";
import {
  IMAGES,
  HEADSHOTS,
  FOS_STEPS,
  SERVICES,
  SERVE,
  CHALLENGES,
  PROCESS,
  MARQUEE
} from "@/data/content";

const Overline = ({ children }) => (
  <p className="text-xs uppercase tracking-[0.25em] text-gold">{children}</p>
);

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const scrollTo = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-navy/85" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-center px-6 pt-28">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
          <Overline>Matlock Advisory Group · Dallas, Texas</Overline>
        </motion.div>

        <h1 className="mt-8 max-w-5xl font-serif text-[13vw] font-semibold leading-[0.92] tracking-tight sm:text-7xl lg:text-8xl">
          <MaskLine delay={0.25}>A Smarter Financial</MaskLine>
          <MaskLine delay={0.4}>System for High-Income</MaskLine>
          <MaskLine delay={0.55}>
            <span className="italic text-gold">Medical Professionals</span>
          </MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-10 max-w-2xl text-lg font-light leading-relaxed text-white/70 sm:text-xl"
        >
          We help CRNAs and other 1099 medical professionals coordinate income, taxes, retirement
          contributions, cash flow, bookkeeping, and reporting through one personalized Financial
          Operating System.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-12 flex flex-col gap-4 sm:flex-row"
        >
          <button
            data-testid="hero-consult-btn"
            onClick={() => scrollTo("#contact")}
            className="group inline-flex items-center justify-center gap-3 rounded-sm bg-gold px-8 py-4 text-sm font-medium text-navy-deep hover:bg-gold-hover transition-colors duration-300"
          >
            Schedule an Introductory Consultation
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-system-btn"
            onClick={() => scrollTo("#system")}
            className="inline-flex items-center justify-center gap-3 rounded-sm border border-white/25 px-8 py-4 text-sm font-medium text-white hover:border-gold hover:text-gold transition-colors duration-300"
          >
            See How the System Works
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
}

function MarqueeStrip() {
  return (
    <div className="relative z-10 border-y border-white/10 bg-navy-deep py-8">
      <Marquee speed={40} gradient={false}>
        {[...MARQUEE, ...MARQUEE].map((t, i) => (
          <span key={i} className="mx-10 font-serif text-3xl italic text-white/70 sm:text-4xl">
            {t}
            <span className="mx-10 text-gold">✦</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

function Problems() {
  const items = [
    "You earn a strong income, but there's no single system holding it together.",
    "Quarterly taxes feel like a guessing game every few months.",
    "Money is spread across accounts with little visibility into cash flow.",
    "Retirement options for your entity are unclear or underused.",
    "Your accountant, bookkeeper, and advisors don't talk to each other.",
    "You want clarity and coordination — not another sales pitch."
  ];
  return (
    <section data-testid="problems-section" className="relative z-10 mx-auto max-w-7xl px-6 py-28 lg:py-36">
      <div className="grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <Overline>The Problem We Solve</Overline>
            <h2 className="mt-6 font-serif text-4xl leading-tight sm:text-5xl">
              High income. <span className="italic text-gold">Low visibility.</span>
            </h2>
            <p className="mt-6 max-w-md text-lg font-light leading-relaxed text-white/60">
              Independent medical professionals earn like business owners but rarely have the
              financial system to match. We build that system.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <div className="grid gap-px overflow-hidden rounded-sm border border-white/10 bg-white/10 sm:grid-cols-2">
            {items.map((t, i) => (
              <Reveal key={i} delay={i * 0.05} className="bg-navy p-8">
                <span className="font-serif text-2xl text-gold/70">0{i + 1}</span>
                <p className="mt-4 text-base font-light leading-relaxed text-white/75">{t}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function System() {
  return (
    <section data-testid="system-section" id="system" className="relative z-10 border-t border-white/10 bg-navy-deep py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <Reveal>
            <Overline>The Financial Operating System™</Overline>
            <h2 className="mt-6 font-serif text-4xl leading-tight sm:text-5xl lg:text-6xl">
              Eight moving parts, coordinated as <span className="italic text-gold">one system.</span>
            </h2>
            <p className="mt-6 text-lg font-light leading-relaxed text-white/60">
              Personalized to each client's income structure, business entity, goals, and financial
              situation — so every decision connects to the next.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-px overflow-hidden rounded-sm border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {FOS_STEPS.map((s, i) => (
            <Reveal key={s.n} delay={(i % 4) * 0.06} className="tracing-card group relative bg-navy-deep p-8">
              <div className="flex items-center justify-between">
                <span className="font-serif text-3xl text-gold">{s.n}</span>
                <span className="h-2 w-2 rounded-full bg-crystal/60 transition-colors duration-300 group-hover:bg-crystal" />
              </div>
              <h3 className="mt-6 font-serif text-2xl">{s.title}</h3>
              <p className="mt-3 text-sm font-light leading-relaxed text-white/55">{s.desc}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Serve() {
  return (
    <section data-testid="serve-section" id="serve" className="relative z-10 py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline>Who We Serve</Overline>
              <h2 className="mt-6 font-serif text-4xl leading-tight sm:text-5xl">
                Built for the way <span className="italic text-gold">Medical Professionals</span> actually earn.
              </h2>
              <p className="mt-6 text-lg font-light leading-relaxed text-white/60">
                Our founder watched these financial realities up close for 25 years. This practice
                exists because of them.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="mt-10 overflow-hidden rounded-sm border border-white/10">
              <div className="relative aspect-[4/3]">
                <img src={IMAGES.crna} alt="Medical professional reviewing information" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-navy/40" />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <div className="grid gap-px overflow-hidden rounded-sm border border-white/10 bg-white/10 sm:grid-cols-2">
              {SERVE.map((s, i) => (
                <Reveal key={s.title} delay={i * 0.05} className="bg-navy p-8">
                  <h3 className="font-serif text-2xl">{s.title}</h3>
                  <p className="mt-3 text-sm font-light leading-relaxed text-white/55">{s.desc}</p>
                </Reveal>
              ))}
            </div>

            <Reveal className="mt-8 rounded-sm border border-white/10 bg-white/[0.03] p-8">
              <p className="text-xs uppercase tracking-[0.2em] text-gold">How We Help</p>
              <ul className="mt-6 grid gap-4 sm:grid-cols-2">
                {CHALLENGES.map((c) => (
                  <li key={c} className="flex items-start gap-3 text-sm font-light text-white/70">
                    <Check size={16} className="mt-0.5 shrink-0 text-gold" />
                    {c}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section data-testid="services-section" id="services" className="relative z-10 border-t border-white/10 bg-navy-deep py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
          <Reveal>
            <Overline>Services</Overline>
            <h2 className="mt-6 max-w-2xl font-serif text-4xl leading-tight sm:text-5xl lg:text-6xl">
              Financial coordination, in plain language.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-sm text-base font-light leading-relaxed text-white/55">
              We help you make informed decisions alongside your tax, legal, and investment
              professionals — without guarantees or hype.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-px overflow-hidden rounded-sm border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={(i % 4) * 0.06} className="tracing-card group bg-navy-deep p-8">
              <h3 className="font-serif text-2xl leading-snug">{s.title}</h3>
              <p className="mt-4 text-sm font-light leading-relaxed text-white/55">{s.desc}</p>
              <ArrowRight size={18} className="mt-6 text-gold opacity-0 transition-all duration-300 group-hover:translate-x-1 group-hover:opacity-100" />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Process() {
  return (
    <section data-testid="process-section" id="process" className="relative z-10 py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal>
          <Overline>The Process</Overline>
          <h2 className="mt-6 max-w-3xl font-serif text-4xl leading-tight sm:text-5xl lg:text-6xl">
            A calm, coordinated path from first call to <span className="italic text-gold">ongoing clarity.</span>
          </h2>
        </Reveal>

        <div className="mt-16 border-t border-white/10">
          {PROCESS.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.04}>
              <div className="group grid grid-cols-12 items-baseline gap-4 border-b border-white/10 py-8 transition-colors duration-300 hover:bg-white/[0.02]">
                <span className="col-span-2 font-serif text-3xl text-gold sm:text-4xl">{p.n}</span>
                <h3 className="col-span-10 font-serif text-2xl sm:col-span-4 sm:text-3xl">{p.title}</h3>
                <p className="col-span-12 text-base font-light leading-relaxed text-white/55 sm:col-span-6">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function About() {
  const bios = [
    {
      name: "Bill Matlock",
      role: "Founder & Fractional CFO",
      image: HEADSHOTS.bill,
      credential: null,
      body: [
        "Bill Matlock founded Matlock Advisory Group after roughly 22 years in the financial advisory industry. He brings decades of experience helping people understand financial decisions, long-term planning, and wealth-building strategies.",
        "Based in Dallas, Texas, Bill is married to Kellee Matlock, who has worked as a CRNA for approximately 25 years across two states. Through Kellee's career, Bill developed a close understanding of the financial realities faced by CRNAs and other high-income medical contractors — the inspiration behind this practice."
      ]
    },
    {
      name: "Hudson Matlock",
      role: "Director of Growth & Client Relations",
      image: HEADSHOTS.hudson,
      credential: "Texas State University Graduate — B.A.I.S., Bachelor of Integrated Studies with a concentration in Healthcare Systems & Knowledge",
      body: [
        "Hudson Matlock leads marketing, business development, and client outreach for Matlock Advisory Group. A Texas State University graduate, he holds a Bachelor of Integrated Studies with a concentration in healthcare systems and knowledge.",
        "That understanding of how healthcare careers actually work shapes how Hudson builds relationships with medical professionals, communicates the firm's services, manages brand growth, and helps prospective clients understand exactly how the Matlock Advisory Group process works."
      ]
    }
  ];
  return (
    <section data-testid="about-section" id="about" className="relative z-10 border-t border-white/10 bg-navy-deep py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Reveal>
              <Overline>About</Overline>
              <h2 className="mt-6 font-serif text-4xl leading-tight sm:text-5xl">
                A family-founded practice.
              </h2>
              <p className="mt-6 text-base font-light leading-relaxed text-white/60">
                Matlock Advisory Group was born from a real understanding of a CRNA household — and
                a belief that successful medical professionals deserve a financial system as capable
                as they are.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-8 grid gap-8">
            {bios.map((b, i) => (
              <Reveal key={b.name} delay={i * 0.1} className="rounded-sm border border-white/10 bg-white/[0.03] p-8 lg:p-10">
                <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
                  {/* Headshot */}
                  <div className="h-32 w-32 shrink-0 overflow-hidden rounded-sm border border-white/10 bg-navy">
                    <img src={b.image} alt={b.name} className="h-full w-full object-cover object-top" />
                  </div>
                  <div>
                    <h3 className="font-serif text-3xl">{b.name}</h3>
                    <p className="mt-1 text-sm uppercase tracking-[0.15em] text-gold">{b.role}</p>
                    {b.credential ? (
                      <p className="mt-3 inline-block rounded-sm border border-crystal/30 bg-crystal/5 px-3 py-1.5 text-xs font-light text-white/70">
                        {b.credential}
                      </p>
                    ) : null}
                    <div className="mt-5 space-y-4">
                      {b.body.map((p, j) => (
                        <p key={j} className="text-base font-light leading-relaxed text-white/65">{p}</p>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section data-testid="contact-section" id="contact" className="relative z-10 py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline>Get Started</Overline>
              <h2 className="mt-6 font-serif text-4xl leading-tight sm:text-5xl lg:text-6xl">
                Get more clarity around your <span className="italic text-gold">1099 finances.</span>
              </h2>
              <p className="mt-6 text-lg font-light leading-relaxed text-white/60">
                Request a no-pressure introductory consultation. We'll learn about your situation and
                show you how the Financial Operating System could work for you.
              </p>
              <div className="mt-10 space-y-2 text-sm text-white/60">
                <p>Prefer to reach out directly?</p>
                {/* Contact information */}
                <a href="tel:501-993-3139" className="block text-lg text-white hover:text-gold transition-colors duration-300">501-993-3139</a>
                <a href="mailto:Bill@MatlockAdvisoryGroup.com" className="block text-lg text-white hover:text-gold transition-colors duration-300">Bill@MatlockAdvisoryGroup.com</a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1} className="rounded-sm border border-white/10 bg-white/[0.03] p-8 lg:p-12">
              <ConsultationForm />
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main data-testid="home-page">
      <Navbar />
      <Hero />
      <MarqueeStrip />
      <Problems />
      <System />
      <Serve />
      <Services />
      <Process />
      <About />
      <Contact />
      <Footer />
    </main>
  );
}
